import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { t } from 'i18next';
import { InferType } from 'yup';
import { editPatientSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useBuildingQuery,
  useEditPatientLocationMutation,
  usePatientLocationQuery,
} from '@common/cache';
import { paths } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';

type IFormShape = InferType<typeof editPatientSchema>;

const initialValues: IFormShape = {
  lives_at_home: 'true',
  room: '',
  street: '',
  suite: '',
  zip_code: '',
  city: '',
  state: '',
  building_id: '',
  building_unit: '',
  county: '',
  country: '',
  phone_number: '',
  fax_number: '',
  google_places_id: '',
  full_address_line: '',
};

interface IUseEditLocationModalProps {
  handleClose: () => void;
  patient_id: string;
}

export const useEditLocationModal = ({
  patient_id,
  handleClose,
}: IUseEditLocationModalProps) => {
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const patientLocation = usePatientLocationQuery({ patient_id });
  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(editPatientSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
    reset,
    setValue,
  } = formMethods;

  const selectedBuildingId = useWatch({
    control: formMethods.control,
    name: 'building_id',
  });

  const buildingQuery = useBuildingQuery({ building_id: selectedBuildingId });

  useEffect(() => {
    if (buildingQuery.data) {
      setValue('phone_number', buildingQuery.data.phone_number ?? '');
      setValue('fax_number', buildingQuery.data.fax_number ?? '');
    }
  }, [buildingQuery.data]);

  const fullAddress = buildingQuery.data?.address?.full_address_line ?? '';

  const { mutateAsync, isPending } = useEditPatientLocationMutation({
    patient_id,
  });

  const buildingUnitOptions = [
    {
      value: 'assisted_living',
      label: t('patients.addModal.building_unit.assisted_living'),
    },
    {
      value: 'memory_care',
      label: t('patients.addModal.building_unit.memory_care'),
    },
    {
      value: 'senior_living',
      label: t('patients.addModal.building_unit.senior_living'),
    },
    {
      value: 'independent_living',
      label: t('patients.addModal.building_unit.independent_living'),
    },
  ];

  const handleCloseAction = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      handleClose();
    }
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: IFormShape) => {
    try {
      await mutateAsync({
        lives_at_home: values.lives_at_home === 'true',
        ...(values.lives_at_home === 'false' && {
          building_id: values.building_id,
        }),
        ...(values.lives_at_home === 'false' && {
          building_unit:
            values.building_unit as paths['/patient']['post']['requestBody']['content']['application/json']['building_unit'],
        }),
        ...(values.lives_at_home === 'false' && {
          room: values.room,
        }),
        ...(values.lives_at_home === 'false' && {
          phone_number: values.phone_number,
        }),
        ...(values.lives_at_home === 'false' && {
          fax_number: values.fax_number,
        }),
        ...(values.lives_at_home === 'true' && {
          google_places_id: values.google_places_id,
        }),
        ...(values.lives_at_home === 'true' && {
          city: values.city,
        }),
        ...(values.lives_at_home === 'true' && {
          state: values.state,
        }),
        ...(values.lives_at_home === 'true' && {
          street: values.street,
        }),
        ...(values.lives_at_home === 'true' && {
          suite: values.suite,
        }),
        ...(values.lives_at_home === 'true' && {
          zip_code: values.zip_code,
        }),
        ...(values.lives_at_home === 'true' && {
          county: values.county,
        }),
        ...(values.lives_at_home === 'true' && {
          country: values.country,
        }),
      });

      setIsSaveChangesModalOpen(false);
      handleClose();
      reset(values);
      toast.success('Patient edited successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    handleClose();
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  useEffect(() => {
    if (patientLocation.data) {
      const {
        address,
        lives_at_home,
        building_id,
        building_unit,
        room,
        phone_number,
        fax_number,
      } = patientLocation.data;

      reset({
        lives_at_home: lives_at_home ? 'true' : 'false',
        building_id: building_id || '',
        building_unit: building_unit || undefined,
        room: room || '',
        phone_number: phone_number || '',
        fax_number: fax_number || '',
        google_places_id: address?.google_places_id ?? '',
        ...address,
        full_address_line: '',
      });
    }
  }, [patientLocation.data]);

  return {
    fullAddress,
    handleSelectLocation,
    formMethods,
    onSubmit,
    isSubmitting,
    submitHandler,
    handleSubmit,
    buildingUnitOptions,
    setValue,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
    handleCloseAction,
    isLoading: patientLocation.isLoading,
  };
};
